import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  filter,
  interval,
  map,
  Observable,
  ReplaySubject,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import { ActivityEventStack } from './activity-event-stack';
import { AnalyticsService } from '../analytics/analytics.service';
import { ZodError } from 'zod';
import { ActivityUtilities } from './models/activity-utilities';
import { HttpService } from '../http/http.service';
import { OperatorService } from '../operator/operator.service';
import { JobService } from '../job/job.service';
import {
  ActivityFeedData,
  ActivityMessageData,
  ActivityMessageDataSchema,
  ActivityType,
} from '../../api/player/schemas/activity-feed';
import { PlayerApi } from '../../api/player/player-api';
import { ResultsTab } from '../theme/theme-config';
import { ThemeService } from '../theme/theme.service';
import { StructuredHintMessage } from 'src/app/shared/types/structured-message';
import { TranslocoService } from '@jsverse/transloco';
import { HintType, PlayHintActivities } from './models/play-hint-activities';
import { ProfileService } from '../profile/profile.service';

class LimitedList {
  public get allItems(): ActivityMessageData[] {
    return this.items;
  }
  public get changes$(): Observable<ActivityMessageData[]> {
    return this.changes.asObservable();
  }

  private items: ActivityMessageData[] = [];
  private readonly changes = new BehaviorSubject<ActivityMessageData[]>([]);

  constructor(private readonly maxItems: number) {
    if (maxItems < 1) {
      throw new Error('maxItems must be at least 1');
    }
  }

  addItems(data: ActivityMessageData[]) {
    this.items.push(...data);

    this.items = this.items.sort(
      (a, b) => ActivityEventStack.getTimestamp(a) - ActivityEventStack.getTimestamp(b),
    );

    if (this.items.length >= this.maxItems) {
      this.items.splice(0, this.items.length - this.maxItems);
    }

    this.changes.next([...this.items]);
  }

  public destroy(): void {
    this.changes.complete();
  }
}

@Injectable({
  providedIn: 'root',
})
export class ActivityFeedService implements OnDestroy {
  private httpService = inject(HttpService);
  private analyticsService = inject(AnalyticsService);
  private operatorService = inject(OperatorService);
  private jobService = inject(JobService);
  private themeService = inject(ThemeService);
  private translocoService = inject(TranslocoService);
  private profileService = inject(ProfileService);

  private readonly displayBufferInterval = 120 * 1000;
  private readonly maxFeedMessages = 100;

  private hasInitialised = false;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private eventStack = new ActivityEventStack();
  private playHintActivities: PlayHintActivities | undefined;
  private playHintActivitiesReady$ = new ReplaySubject<PlayHintActivities>(1);

  oneSecSyncInterval$: Observable<Date>;
  feedMessages = new LimitedList(this.maxFeedMessages);

  constructor() {
    this.oneSecSyncInterval$ = interval(1000).pipe(
      map(() => new Date()),
      shareReplay(1), // Share the same observable across all subscribers
    );
  }

  async initialize(): Promise<void> {
    if (this.hasInitialised) {
      return;
    }
    this.hasInitialised = true;

    timer(0, this.displayBufferInterval)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.updateActivityFeed();
      });

    this.oneSecSyncInterval$.pipe(takeUntil(this.destroyed$)).subscribe((now) => {
      this.processEvents(now);
    });

    const jwtCurrency = await this.profileService.jwtCurrency();

    this.playHintActivities = new PlayHintActivities(
      this.operatorService.gameId,
      this.translocoService,
      this.themeService,
      jwtCurrency,
    );
    this.playHintActivitiesReady$.next(this.playHintActivities);
  }

  ngOnDestroy() {
    this.cleanup();
  }

  public fetchHints(isInitial: boolean): Observable<StructuredHintMessage[]> {
    return this.playHintActivitiesReady$.pipe(
      take(1),
      switchMap((playHintActivities) => {
        // Wait for feedMessages to have items
        return this.feedMessages.changes$.pipe(
          // Only proceed when there are actual messages
          filter((messages) => messages.length > 0),
          take(1),
          map((messages) => {
            if (isInitial) {
              playHintActivities.populateMessageActivities(messages);
              playHintActivities.resetAll();
            }

            const getRandomCount = (min: number, max: number): number =>
              Math.floor(Math.random() * (max - min + 1)) + min;

            const resultsTabOrder = this.themeService.resultsTabOrder();
            const hasWorldwideTab = resultsTabOrder?.includes(ResultsTab.worldwide) ?? false;

            return [
              ...(hasWorldwideTab
                ? playHintActivities.popFirstWinMessages(getRandomCount(1, 3))
                : []),
              ...Array.from({ length: getRandomCount(2, 3) }, () =>
                playHintActivities.getRandomHint(HintType.Tip),
              ),
              ...(hasWorldwideTab
                ? Array.from({ length: getRandomCount(1, 2) }, () =>
                    playHintActivities.getRandomMessage(),
                  ).flatMap((message) => message ?? [])
                : []),
              ...Array.from({ length: getRandomCount(2, 2) }, () =>
                playHintActivities.getRandomHint(HintType.Tip),
              ),
              playHintActivities.getRandomHint(HintType.Cta),
            ];
          }),
        );
      }),
    );
  }

  private cleanup() {
    this.feedMessages.destroy();

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private processEvents(now: Date): void {
    if (this.eventStack.hasEvents()) {
      let dueEvents = this.eventStack
        .popDueEvents(now)
        .filter(
          (dueEvent) =>
            !this.feedMessages.allItems.some((item) => item.activityId === dueEvent.activityId),
        );
      if (this.themeService.isAnonymous()) {
        dueEvents = dueEvents.filter(
          (message) =>
            ActivityUtilities.isWinActivity(message.data.type) ||
            message.data.type === ActivityType.NewPlayer,
        );
      }
      this.feedMessages.addItems(dueEvents);
      this.playHintActivities?.appendMessageActivities(dueEvents);
    }
  }

  private async updateActivityFeed() {
    const data = await this.fetchActivityFeed();
    if (data) {
      const activityMessages = this.parseActivityFeed(data);

      // Stretch the dates so that they appear within 'now' and 'now + bufferInterval'
      const transformedMessages = ActivityUtilities.transformDisplayDates(
        activityMessages,
        Date.now(),
        this.displayBufferInterval,
      );

      // Push new activities onto the event stack
      this.eventStack.pushEvents(transformedMessages);
    }
  }

  private async fetchActivityFeed(): Promise<ActivityFeedData | undefined> {
    const gameId = this.operatorService.gameId;
    const job = this.jobService.create(async () =>
      PlayerApi.request(this.httpService, this.analyticsService).activityFeed(gameId),
    );
    return await job.runNoThrow();
  }

  private parseActivityFeed(activityFeed: ActivityFeedData): ActivityMessageData[] {
    const activityMessages: ActivityMessageData[] = [];

    const activities = activityFeed.activities;
    for (const time in activities) {
      const activitiesAtTime = activities[time];

      for (const activityId in activitiesAtTime) {
        try {
          const cachedActivity = activitiesAtTime[activityId];
          ActivityMessageDataSchema.parse(cachedActivity);
          activityMessages.push(cachedActivity);
        } catch (error) {
          if (error instanceof ZodError) {
            console.error('Zod Validation Error: ', error.issues);
          }
          this.analyticsService.handleError(error, 'Parse activity feed');
        }
      }
    }
    return activityMessages;
  }
}
